import { useEffect, useRef, useState } from "react";
import vwr from "vwr.js";
import { Loader2 } from "lucide-react";

interface VwrPreviewProps {
  url: string;
  /** Optional className applied to the wrapper that vwr renders into. */
  className?: string;
}

/**
 * Render a PDF (or other supported document) with vwr.js, the same
 * viewer reflex-admin uses for booking attachments. Replaces the
 * browser's default `<iframe src=*.pdf>` viewer with a controllable
 * canvas-based renderer.
 *
 * vwr.render(container, url, { onLoad, onError }) returns an instance
 * with a destroy() method. We hold it in a ref so we can tear down
 * cleanly when the URL changes or the component unmounts.
 */
export function VwrPreview({ url, className }: VwrPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<{ destroy: () => void } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current || !url) return;

    setLoading(true);
    setError(null);

    if (viewerRef.current) {
      try {
        viewerRef.current.destroy();
      } catch {
        /* ignore */
      }
      viewerRef.current = null;
    }

    containerRef.current.innerHTML = "";

    try {
      viewerRef.current = vwr.render(containerRef.current, url, {
        onLoad: () => setLoading(false),
        onError: (err: { url: string; message: string; code: string }) => {
          setError(err.message || "Couldn't load preview");
          setLoading(false);
        },
      });
      // Belt-and-braces: vwr's onLoad isn't always reliable, so clear
      // the spinner after a short timeout if nothing has happened.
      const fallback = window.setTimeout(() => setLoading(false), 2000);
      return () => {
        window.clearTimeout(fallback);
        if (viewerRef.current) {
          try {
            viewerRef.current.destroy();
          } catch {
            /* ignore */
          }
          viewerRef.current = null;
        }
      };
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load preview");
      setLoading(false);
    }
  }, [url]);

  return (
    <div className={`relative ${className ?? ""}`}>
      <div
        ref={containerRef}
        className="w-full h-full bg-paper-hi overflow-hidden"
      />
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-paper-hi/60 backdrop-blur-sm rounded pointer-events-none">
          <Loader2
            size={18}
            strokeWidth={1.8}
            className="animate-spin text-tide"
          />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-paper-hi rounded text-[12.5px] text-rust px-4 text-center">
          {error}
        </div>
      )}
    </div>
  );
}
